import type { UploadTicket } from "@airspace/shared";
import { ApiRequestError } from "./client.js";
import type { UploadBackend } from "./transfer.js";

/** Was eine Datenquelle kann. Die Oberflaeche richtet sich danach. */
export interface ProviderCapabilities {
  supportsFolders: boolean;
  supportsSearch: boolean;
  supportsUpload: boolean;
  supportsDownload: boolean;
  supportsOverwrite: boolean;
  supportsOriginalDownloadOnly: boolean;
  supportsCreateFolder: boolean;
}

export interface VaultProvider {
  id: string;
  label: string;
  capabilities: ProviderCapabilities;
  /** Ob die Datenquelle gerade antwortet. Lokale Quellen sind immer erreichbar. */
  reachable: boolean;
  /** Grund, wenn nicht erreichbar. */
  detail: string | null;
}

export interface VaultEntry {
  id: string;
  name: string;
  kind: "file" | "folder";
  sizeBytes: number | null;
  modifiedAt: string | null;
  mimeType: string | null;
}

export interface ListResult {
  folderId: string;
  parentId: string | null;
  entries: VaultEntry[];
}

export interface SearchResult {
  entries: VaultEntry[];
  /** Ob die Suche vorzeitig abgebrochen wurde. */
  truncated: boolean;
}

/** Lebenslauf eines Vault-Uploads. */
export type VaultUploadStatus =
  | "staging"
  | "verifying"
  | "approving"
  | "writing"
  /** Nachweislich am Ziel. */
  | "done"
  /**
   * Von der Datenquelle entgegengenommen, Ausgang offen.
   *
   * Paperless verarbeitet Dokumente nach der Annahme selbst weiter. Ein Erfolg
   * waere zu diesem Zeitpunkt eine Behauptung, die niemand geprueft hat.
   */
  | "accepted"
  | "failed"
  | "denied"
  | "expired";

export interface VaultUpload {
  uploadId: string;
  status: VaultUploadStatus;
  name: string;
  size: number;
  providerId: string;
  receivedBytes: number;
  offset?: number;
  sha256?: string | null;
  detail: string | null;
  resultName: string | null;
  /** Ob das Schreiben wiederholt werden kann, ohne erneut hochzuladen. */
  retryable?: boolean;
}

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? "unknown_error",
      error?.message ?? "Unerwarteter Fehler.",
    );
  }
  return payload as T;
}

export const vaultApi = {
  providers: () => json<{ providers: VaultProvider[] }>("GET", "/api/vault/providers"),

  list: (providerId: string, folderId: string) =>
    json<ListResult>(
      "GET",
      `/api/vault/${encodeURIComponent(providerId)}/list?folder=${encodeURIComponent(folderId)}`,
    ),

  search: (providerId: string, query: string) =>
    json<SearchResult>(
      "GET",
      `/api/vault/${encodeURIComponent(providerId)}/search?q=${encodeURIComponent(query)}`,
    ),

  createFolder: (providerId: string, parentId: string, name: string) =>
    json<VaultEntry>("POST", `/api/vault/${encodeURIComponent(providerId)}/folders`, {
      parentId,
      name,
    }),

  beginUpload: (input: { providerId: string; folderId: string; name: string; size: number }) =>
    json<UploadTicket>("POST", "/api/vault/uploads", input),

  uploadStatus: (uploadId: string) =>
    json<VaultUpload>("GET", `/api/vault/uploads/${encodeURIComponent(uploadId)}`),

  listUploads: () => json<{ uploads: VaultUpload[] }>("GET", "/api/vault/uploads"),

  /** Wiederholt das Schreiben zur Datenquelle, ohne erneut hochzuladen. */
  retryUpload: (uploadId: string) =>
    json<{ status: string }>("POST", `/api/vault/uploads/${encodeURIComponent(uploadId)}/retry`),
};

/** Adresse zum Herunterladen eines Eintrags. */
export function vaultDownloadUrl(providerId: string, entryId: string): string {
  return `/api/vault/${encodeURIComponent(providerId)}/download?id=${encodeURIComponent(entryId)}`;
}

/**
 * Zugang zu den Vault-Endpunkten fuer den Chunk-Uploader.
 *
 * Dasselbe Protokoll wie beim Throw, nur andere Adressen -- deshalb genuegt
 * hier diese kleine Zuordnung statt einer zweiten Uploader-Implementierung.
 */
export function vaultUploadBackend(input: {
  providerId: string;
  folderId: string;
  name: string;
  size: number;
}): UploadBackend {
  let ticket: UploadTicket | null = null;

  return {
    createTicket: async () => {
      // Nur beim ersten Mal anmelden. Ein erneuter Aufruf nach einer Stoerung
      // soll denselben Upload fortsetzen, nicht einen zweiten beginnen.
      ticket ??= await vaultApi.beginUpload(input);
      return ticket;
    },
    chunkUrl: (uploadId) => `/api/vault/uploads/${encodeURIComponent(uploadId)}`,
    currentOffset: async (uploadId) => (await vaultApi.uploadStatus(uploadId)).offset ?? 0,
  };
}

/** Verstaendlicher Text zum Zustand eines Uploads. */
export const UPLOAD_STATUS_TEXT: Record<VaultUploadStatus, string> = {
  staging: "Überträgt",
  verifying: "Prüfsumme wird berechnet",
  approving: "Wartet auf Freigabe",
  writing: "Wird abgelegt",
  done: "Abgelegt",
  accepted: "Angenommen",
  failed: "Fehlgeschlagen",
  denied: "Nicht freigegeben",
  expired: "Abgelaufen",
};
